import React, { useState } from 'react';
import { Loader2, Link as LinkIcon, Copy } from 'lucide-react';
import { useHousesStore } from '../../store/useStore.js';

export default function ShareButton({ houseId }) {
  const { createShareLink } = useHousesStore();
  const [shareUrl, setShareUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');

  const buildUrl = (relative) => {
    if (relative.startsWith('http')) return relative;
    if (typeof window === 'undefined') return relative;
    return `${window.location.origin}${relative}`;
  };

  const handleGenerate = async () => {
    if (!houseId) return;
    setLoading(true);
    setError('');
    setCopied(false);

    try {
      const shareLink = await createShareLink(houseId);
      const fullUrl = buildUrl(shareLink.url);
      setShareUrl(fullUrl);

      if (navigator.share) {
        try {
          await navigator.share({
            title: 'Check out this AvRi house analysis',
            url: fullUrl,
          });
          setCopied(true);
          setLoading(false);
          return;
        } catch (shareError) {
          // If user cancels native share we fall back to copy UI
        }
      }
    } catch (err) {
      setError(err.message || 'Unable to create share link.');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch (err) {
      setError('Copy failed. Try manually selecting the link.');
    }
  };

  return (
    <div className="space-y-3">
      <button
        type="button"
        onClick={handleGenerate}
        disabled={loading}
        className="inline-flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-indigo-700 disabled:bg-gray-300"
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <LinkIcon className="w-4 h-4" />}
        Generate Share Link
      </button>

      {error && <p className="text-sm text-red-600">{error}</p>}

      {shareUrl && (
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 bg-indigo-50 border border-indigo-100 rounded-lg px-4 py-3 text-sm text-indigo-700">
          <span className="truncate">{shareUrl}</span>
          <button
            type="button"
            onClick={handleCopy}
            className="inline-flex items-center gap-2 text-indigo-600 hover:text-indigo-700 font-semibold"
          >
            <Copy className="w-4 h-4" />
            {copied ? 'Copied!' : 'Copy'}
          </button>
        </div>
      )}
    </div>
  );
}
